import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { RootState } from "app/store";
import type { OutreachScheduleFilters } from "services/types/collections.type";

function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

const currentMonday = getMonday(new Date());
const currentSundayPlusOne = new Date(currentMonday);
currentSundayPlusOne.setDate(currentSundayPlusOne.getDate() + 7);

interface OutreachScheduleFiltersState {
  sortField: string;
  sortDirection: "asc" | "desc";
  page: number;
  pageSize: number;
  searchValue: string;
  customFilters: Partial<OutreachScheduleFilters>;
  dateFrom: string;
  dateTo: string;
}

const initialState: OutreachScheduleFiltersState = {
  sortField: "scheduled_at",
  sortDirection: "asc",
  page: 0,
  pageSize: 25,
  searchValue: "",
  customFilters: {},
  dateFrom: formatDateParam(currentMonday),
  dateTo: formatDateParam(currentSundayPlusOne)
};

const outreachScheduleFiltersSlice = createSlice({
  name: "outreachScheduleFilters",
  initialState,
  reducers: {
    setSort(
      state,
      action: PayloadAction<{ field: string; direction: "asc" | "desc" }>
    ) {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
      state.page = 0;
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.page = 0;
    },
    setSearchValue(state, action: PayloadAction<string>) {
      state.searchValue = action.payload;
      state.page = 0;
    },
    setCustomFilter(
      state,
      action: PayloadAction<{
        field: keyof OutreachScheduleFilters;
        value: any;
      }>
    ) {
      if (action.payload.value === undefined || action.payload.value === null) {
        delete state.customFilters[action.payload.field];
      } else {
        (state.customFilters as any)[action.payload.field] =
          action.payload.value;
      }
      state.page = 0;
    },
    clearCustomFilters(state) {
      state.customFilters = {};
      state.page = 0;
    },
    setDateRange(state, action: PayloadAction<{ from: string; to: string }>) {
      state.dateFrom = action.payload.from;
      state.dateTo = action.payload.to;
      state.page = 0;
    },
    goToPrevWeek(state) {
      const from = parseLocalDate(state.dateFrom);
      from.setDate(from.getDate() - 7);
      const to = parseLocalDate(state.dateTo);
      to.setDate(to.getDate() - 7);
      state.dateFrom = formatDateParam(from);
      state.dateTo = formatDateParam(to);
      state.page = 0;
    },
    goToNextWeek(state) {
      const from = parseLocalDate(state.dateFrom);
      from.setDate(from.getDate() + 7);
      const to = parseLocalDate(state.dateTo);
      to.setDate(to.getDate() + 7);
      state.dateFrom = formatDateParam(from);
      state.dateTo = formatDateParam(to);
      state.page = 0;
    },
    goToCurrentWeek(state) {
      const monday = getMonday(new Date());
      const sundayPlusOne = new Date(monday);
      sundayPlusOne.setDate(sundayPlusOne.getDate() + 7);
      state.dateFrom = formatDateParam(monday);
      state.dateTo = formatDateParam(sundayPlusOne);
      state.page = 0;
    },
    resetFilters() {
      return initialState;
    }
  }
});

export const selectOutreachSortField = (state: RootState) =>
  state.outreachScheduleFilters.sortField;

export const selectOutreachSortDirection = (state: RootState) =>
  state.outreachScheduleFilters.sortDirection;

export const selectOutreachPage = (state: RootState) =>
  state.outreachScheduleFilters.page;

export const selectOutreachPageSize = (state: RootState) =>
  state.outreachScheduleFilters.pageSize;

export const selectOutreachSearchValue = (state: RootState) =>
  state.outreachScheduleFilters.searchValue;

export const selectOutreachCustomFilters = (state: RootState) =>
  state.outreachScheduleFilters.customFilters;

export const selectOutreachDateFrom = (state: RootState) =>
  state.outreachScheduleFilters.dateFrom;

export const selectOutreachDateTo = (state: RootState) =>
  state.outreachScheduleFilters.dateTo;

export const {
  setSort,
  setPage,
  setPageSize,
  setSearchValue,
  setCustomFilter,
  clearCustomFilters,
  setDateRange,
  goToPrevWeek,
  goToNextWeek,
  goToCurrentWeek,
  resetFilters
} = outreachScheduleFiltersSlice.actions;

export default outreachScheduleFiltersSlice.reducer;
